import { create } from "zustand";
import {
  LatexDocument,
  LatexCursor,
  InsertionType,
  createEmptyDocument,
  createRootCursor,
} from "../types/latexAst";
import {
  applyInsertion,
  insertText,
  backspace,
  moveLeft,
  moveRight,
  moveUp,
  moveDown,
  tabForward,
} from "../utils/latexAstOperations";
import { serializeToLatex } from "../utils/latexSerializer";

const MAX_UNDO_HISTORY = 3;

interface HistoryEntry {
  doc: LatexDocument;
  cursor: LatexCursor;
}

interface StudentInputState {
  // AST-based storage
  documents: Record<string, LatexDocument>; // inputKey -> AST document
  cursors: Record<string, LatexCursor>; // inputKey -> cursor position

  // Undo/redo stacks per input
  undoStacks: Record<string, HistoryEntry[]>;
  redoStacks: Record<string, HistoryEntry[]>;

  // Legacy string-based storage (computed from AST for backward compatibility)
  inputs: Record<string, string>; // inputKey -> serialized LaTeX string

  // Mappings
  inputKeyToActionId: Record<string, string>; // inputKey -> actionId (for backend)

  // Processing state
  isProcessing: boolean;
  processingInputs: Set<string>; // Individual inputs being checked
  revealedInputs: Set<string>; // Inputs with revealed answers
  submittedInputValues: Record<string, string>; // inputKey -> value at time of last submission
  lastEditedInputKey: string | null; // inputKey of the most recently edited input box

  // Keyboard state
  isKeyboardOpen: boolean;
  activeInputId: string | null;
  activeInputIsMath: boolean;

  // AST operations
  insertAtCursor: (inputKey: string, insertion: InsertionType) => void;
  backspaceAtCursor: (inputKey: string) => void;
  moveCursor: (
    inputKey: string,
    direction: "left" | "right" | "up" | "down",
  ) => void;
  setCursorAbsolute: (inputKey: string, cursor: LatexCursor) => void;
  tabNavigation: (inputKey: string) => void;

  // Document access
  getDocument: (inputKey: string) => LatexDocument;
  getCursor: (inputKey: string) => LatexCursor;
  getLatexString: (inputKey: string) => string;

  // Legacy compatibility method (for native keyboard input on non-touch devices)
  setInput: (inputKey: string, value: string, cursorPos?: number) => void;
  /** Empties the box as one undo step, so undo brings the old value back. */
  clearInput: (inputKey: string) => void;

  // Registration and mapping
  registerInput: (inputKey: string, actionId: string) => void;
  getActionId: (inputKey: string) => string | undefined;
  getInputKeyByActionId: (actionId: string) => string | undefined;

  // State management
  clearInputs: () => void;
  clearInputsKeepKeyboard: () => void;
  getInputs: () => Record<string, string>;
  getInputsForBackend: () => Record<string, string>;

  // Processing
  setProcessing: (processing: boolean) => void;
  setInputProcessing: (inputKey: string, processing: boolean) => void;
  markInputRevealed: (inputKey: string) => void;
  unmarkInputRevealed: (inputKey: string) => void;
  isInputRevealed: (inputKey: string) => boolean;
  markInputSubmitted: (inputKey: string, value: string) => void;
  unmarkInputSubmitted: (inputKey: string) => void;

  // Undo/redo
  undo: (inputKey: string) => void;
  redo: (inputKey: string) => void;

  // Keyboard actions
  openKeyboard: (inputId: string, isMath: boolean) => void;
  closeKeyboard: () => void;
}

/**
 * Push current doc/cursor onto the undo stack for an input, clear redo.
 */
function pushUndo(
  state: StudentInputState,
  inputKey: string,
): Partial<StudentInputState> {
  const doc = state.documents[inputKey];
  const cursor = state.cursors[inputKey];
  if (!doc || !cursor) return {};

  const stack = (state.undoStacks[inputKey] || []).slice();
  stack.unshift({ doc, cursor });
  if (stack.length > MAX_UNDO_HISTORY) stack.length = MAX_UNDO_HISTORY;

  return {
    undoStacks: { ...state.undoStacks, [inputKey]: stack },
    redoStacks: { ...state.redoStacks, [inputKey]: [] },
  };
}

/**
 * Helper to update the serialized inputs string when AST changes
 */
function updateSerializedInput(
  state: StudentInputState,
  inputKey: string,
  doc: LatexDocument,
): Partial<StudentInputState> {
  const serialized = serializeToLatex(doc);
  return {
    inputs: { ...state.inputs, [inputKey]: serialized },
  };
}

export const useStudentInputStore = create<StudentInputState>((set, get) => ({
  // AST storage
  documents: {},
  cursors: {},

  // Undo/redo stacks
  undoStacks: {},
  redoStacks: {},

  // Legacy storage (kept in sync with AST)
  inputs: {},

  // Mappings
  inputKeyToActionId: {},

  // Processing state
  isProcessing: false,
  processingInputs: new Set(),
  revealedInputs: new Set(),
  submittedInputValues: {},
  lastEditedInputKey: null,

  // Keyboard state
  isKeyboardOpen: false,
  activeInputId: null,
  activeInputIsMath: true,

  // ============================================================================
  // AST OPERATIONS
  // ============================================================================

  insertAtCursor: (inputKey: string, insertion: InsertionType) => {
    set((state) => {
      const doc = state.documents[inputKey] || createEmptyDocument();
      const cursor = state.cursors[inputKey] || createRootCursor();
      const undoUpdate = pushUndo(state, inputKey);

      const result = applyInsertion(doc, cursor, insertion);

      return {
        ...undoUpdate,
        documents: { ...state.documents, [inputKey]: result.doc },
        cursors: { ...state.cursors, [inputKey]: result.cursor },
        lastEditedInputKey: inputKey,
        ...updateSerializedInput(state, inputKey, result.doc),
      };
    });
  },

  backspaceAtCursor: (inputKey: string) => {
    set((state) => {
      const doc = state.documents[inputKey];
      const cursor = state.cursors[inputKey];

      if (!doc || !cursor) return state;

      const undoUpdate = pushUndo(state, inputKey);
      const result = backspace(doc, cursor);

      return {
        ...undoUpdate,
        documents: { ...state.documents, [inputKey]: result.doc },
        cursors: { ...state.cursors, [inputKey]: result.cursor },
        lastEditedInputKey: inputKey,
        ...updateSerializedInput(state, inputKey, result.doc),
      };
    });
  },

  moveCursor: (
    inputKey: string,
    direction: "left" | "right" | "up" | "down",
  ) => {
    set((state) => {
      const doc = state.documents[inputKey];
      const cursor = state.cursors[inputKey];

      if (!doc || !cursor) return state;

      let newCursor: LatexCursor;
      switch (direction) {
        case "left":
          newCursor = moveLeft(doc, cursor);
          break;
        case "right":
          newCursor = moveRight(doc, cursor);
          break;
        case "up":
          newCursor = moveUp(doc, cursor);
          break;
        case "down":
          newCursor = moveDown(doc, cursor);
          break;
      }

      return {
        cursors: { ...state.cursors, [inputKey]: newCursor },
      };
    });
  },

  setCursorAbsolute: (inputKey: string, newCursor: LatexCursor) => {
    set((state) => ({
      cursors: { ...state.cursors, [inputKey]: newCursor },
    }));
  },

  tabNavigation: (inputKey: string) => {
    set((state) => {
      const doc = state.documents[inputKey];
      const cursor = state.cursors[inputKey];

      if (!doc || !cursor) return state;

      const newCursor = tabForward(doc, cursor);

      return {
        cursors: { ...state.cursors, [inputKey]: newCursor },
      };
    });
  },

  // ============================================================================
  // DOCUMENT ACCESS
  // ============================================================================

  getDocument: (inputKey: string) => {
    return get().documents[inputKey] || createEmptyDocument();
  },

  getCursor: (inputKey: string) => {
    return get().cursors[inputKey] || createRootCursor();
  },

  getLatexString: (inputKey: string) => {
    const doc = get().documents[inputKey];
    if (!doc) return "";
    return serializeToLatex(doc);
  },

  // ============================================================================
  // LEGACY COMPATIBILITY
  // ============================================================================

  /**
   * Legacy setInput - converts string to AST
   * Used by non-math inputs and native keyboard on non-touch devices
   */
  setInput: (inputKey: string, value: string, cursorPos?: number) => {
    set((state) => {
      // For simple text inputs, we create a document with text nodes
      let doc = createEmptyDocument();
      let lastCursor = createRootCursor();

      for (const char of value) {
        const result = insertText(doc, lastCursor, char);
        doc = result.doc;
        lastCursor = result.cursor;
      }
      const cursor: LatexCursor = {
        containerId: "root",
        containerField: "nodes",
        offset: cursorPos !== undefined ? cursorPos : doc.nodes.length,
      };
      return {
        documents: { ...state.documents, [inputKey]: doc },
        cursors: { ...state.cursors, [inputKey]: cursor },
        inputs: { ...state.inputs, [inputKey]: value },
        lastEditedInputKey: inputKey,
      };
    });
  },

  // What setInput(inputKey, "") leaves, plus the undo step it skips.
  clearInput: (inputKey: string) => {
    set((state) => ({
      ...pushUndo(state, inputKey),
      documents: { ...state.documents, [inputKey]: createEmptyDocument() },
      cursors: { ...state.cursors, [inputKey]: createRootCursor() },
      inputs: { ...state.inputs, [inputKey]: "" },
      lastEditedInputKey: inputKey,
    }));
  },

  // ============================================================================
  // REGISTRATION AND MAPPING
  // ============================================================================

  registerInput: (inputKey: string, actionId: string) => {
    set((state) => ({
      inputKeyToActionId: { ...state.inputKeyToActionId, [inputKey]: actionId },
      // Initialize empty document and cursor if not exists
      documents: state.documents[inputKey]
        ? state.documents
        : { ...state.documents, [inputKey]: createEmptyDocument() },
      cursors: state.cursors[inputKey]
        ? state.cursors
        : { ...state.cursors, [inputKey]: createRootCursor() },
    }));
  },

  getActionId: (inputKey: string) => {
    return get().inputKeyToActionId[inputKey];
  },

  getInputKeyByActionId: (actionId: string) => {
    const mapping = get().inputKeyToActionId;
    for (const [inputKey, aId] of Object.entries(mapping)) {
      if (aId === actionId) {
        return inputKey;
      }
    }
    return undefined;
  },

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  clearInputs: () => {
    set({
      documents: {},
      cursors: {},
      inputs: {},
      inputKeyToActionId: {},
      isProcessing: false,
      processingInputs: new Set(),
      revealedInputs: new Set(),
      submittedInputValues: {},
      lastEditedInputKey: null,
      isKeyboardOpen: false,
      activeInputId: null,
      undoStacks: {},
      redoStacks: {},
    });
  },

  clearInputsKeepKeyboard: () => {
    set({
      documents: {},
      cursors: {},
      inputs: {},
      inputKeyToActionId: {},
      isProcessing: false,
      processingInputs: new Set(),
      revealedInputs: new Set(),
      submittedInputValues: {},
      lastEditedInputKey: null,
      undoStacks: {},
      redoStacks: {},
      // Keep keyboard state unchanged
    });
  },

  getInputs: () => get().inputs,

  getInputsForBackend: () => {
    const state = get();
    const result: Record<string, string> = {};
    for (const [inputKey, doc] of Object.entries(state.documents)) {
      const actionId = state.inputKeyToActionId[inputKey];
      if (actionId) {
        result[actionId] = serializeToLatex(doc);
      }
    }
    return result;
  },

  // ============================================================================
  // PROCESSING STATE
  // ============================================================================

  setProcessing: (processing: boolean) => {
    set({ isProcessing: processing });
  },

  setInputProcessing: (inputKey: string, processing: boolean) => {
    set((state) => {
      const newSet = new Set(state.processingInputs);
      if (processing) {
        newSet.add(inputKey);
      } else {
        newSet.delete(inputKey);
      }
      return { processingInputs: newSet };
    });
  },

  /**
   * Mark an input as revealed (answer shown).
   * Pure state update only - call applyInputRevealedDOM() separately for DOM changes.
   */
  markInputRevealed: (inputKey: string) => {
    set((state) => {
      const newSet = new Set(state.revealedInputs);
      newSet.add(inputKey);
      return { revealedInputs: newSet };
    });
  },

  /**
   * Unmark an input as revealed (navigating backward).
   * Pure state update only - call applyInputUnrevealedDOM() separately for DOM changes.
   * Clears input data atomically to avoid race conditions.
   */
  unmarkInputRevealed: (inputKey: string) => {
    set((state) => {
      const newSet = new Set(state.revealedInputs);
      newSet.delete(inputKey);

      return {
        revealedInputs: newSet,
        // Reset input data to empty state (subscription will sync DOM automatically)
        documents: { ...state.documents, [inputKey]: createEmptyDocument() },
        cursors: { ...state.cursors, [inputKey]: createRootCursor() },
        inputs: { ...state.inputs, [inputKey]: "" },
      };
    });
  },

  isInputRevealed: (inputKey: string) => {
    return get().revealedInputs.has(inputKey);
  },

  markInputSubmitted: (inputKey: string, value: string) => {
    set((state) => ({
      submittedInputValues: {
        ...state.submittedInputValues,
        [inputKey]: value,
      },
    }));
  },

  // Lifts an optimistic submission mark. Called when the backend reports it never
  // produced a verdict for this answer, so the student can send the same value again.
  unmarkInputSubmitted: (inputKey: string) => {
    set((state) => {
      if (!(inputKey in state.submittedInputValues)) return state;
      const { [inputKey]: _removed, ...remaining } = state.submittedInputValues;
      return { submittedInputValues: remaining };
    });
  },

  // ============================================================================
  // UNDO / REDO
  // ============================================================================

  undo: (inputKey: string) => {
    set((state) => {
      const stack = state.undoStacks[inputKey];
      if (!stack || stack.length === 0) return state;

      const currentDoc = state.documents[inputKey];
      const currentCursor = state.cursors[inputKey];

      const [entry, ...rest] = stack;

      // Push current state onto redo stack
      const redoStack = (state.redoStacks[inputKey] || []).slice();
      if (currentDoc && currentCursor) {
        redoStack.unshift({ doc: currentDoc, cursor: currentCursor });
      }

      return {
        documents: { ...state.documents, [inputKey]: entry.doc },
        cursors: { ...state.cursors, [inputKey]: entry.cursor },
        undoStacks: { ...state.undoStacks, [inputKey]: rest },
        redoStacks: { ...state.redoStacks, [inputKey]: redoStack },
        lastEditedInputKey: inputKey,
        ...updateSerializedInput(state, inputKey, entry.doc),
      };
    });
  },

  redo: (inputKey: string) => {
    set((state) => {
      const stack = state.redoStacks[inputKey];
      if (!stack || stack.length === 0) return state;

      const currentDoc = state.documents[inputKey];
      const currentCursor = state.cursors[inputKey];

      const [entry, ...rest] = stack;

      // Push current state onto undo stack
      const undoStack = (state.undoStacks[inputKey] || []).slice();
      if (currentDoc && currentCursor) {
        undoStack.unshift({ doc: currentDoc, cursor: currentCursor });
      }

      return {
        documents: { ...state.documents, [inputKey]: entry.doc },
        cursors: { ...state.cursors, [inputKey]: entry.cursor },
        undoStacks: { ...state.undoStacks, [inputKey]: undoStack },
        redoStacks: { ...state.redoStacks, [inputKey]: rest },
        lastEditedInputKey: inputKey,
        ...updateSerializedInput(state, inputKey, entry.doc),
      };
    });
  },

  // ============================================================================
  // KEYBOARD ACTIONS
  // ============================================================================

  openKeyboard: (inputId: string, isMath: boolean) => {
    if (!isMath) return;
    set({
      isKeyboardOpen: true,
      activeInputId: inputId,
      activeInputIsMath: isMath,
    });
  },

  closeKeyboard: () => {
    // Get the currently active input before clearing state
    const currentActiveId = get().activeInputId;

    // Update store state
    set({
      isKeyboardOpen: false,
      activeInputId: null,
    });

    // Blur the active input element to remove focus
    if (currentActiveId) {
      const inputElement = document.querySelector(
        `input[data-input-key="${currentActiveId}"]`,
      ) as HTMLInputElement | null;

      if (inputElement) {
        inputElement.blur();
      }
    }
  },
}));
